import { MouseEvent } from 'react'

export const preventDefault = <T>(e: MouseEvent<T>) => {
  e.preventDefault()

  return <R>(eventHandler: (e: MouseEvent<T>) => R) => eventHandler(e)
}

// @see https://jbee.io/web/optimize-scroll-event/
export function toFit(
  cb: Function,
  { dismissCondition = () => false, triggerCondition = () => true },
) {
  if (!cb) {
    throw Error('Invalid required arguments')
  }

  let tick = false

  return function() {
    if (tick) {
      return
    }

    tick = true

    return requestAnimationFrame(() => {
      if (dismissCondition()) {
        tick = false

        return
      }

      if (triggerCondition()) {
        tick = false

        return cb()
      }
    })
  }
}

export type Procedure = (...args: any[]) => void

export type DebounceOptions = {
  isImmediate: boolean
}

export function debounce<F extends Procedure>(
  func: F,
  waitMilliseconds = 100,
  options: DebounceOptions = {
    isImmediate: false,
  },
): F {
  let timeoutId: number | undefined

  return function(this: any, ...args: any[]) {
    const context = this

    const doLater = function() {
      timeoutId = undefined
      if (!options.isImmediate) {
        func.apply(context, args)
      }
    }

    const shouldCallNow = options.isImmediate && timeoutId === undefined

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }

    timeoutId = window.setTimeout(doLater, waitMilliseconds)

    if (shouldCallNow) {
      func.apply(context, args)
    }
  } as any
}

// @see https://bts.linecorp.com/browse/LFBID-107
export const preventEvents = (milliseconds: number | void) => {
  document.body.classList.add('prevent-events')

  setTimeout(() => {
    document.body.classList.remove('prevent-events')
  }, milliseconds || 300)
}
